import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

interface Question {
  id: number;
  question: string;
  options: string[];
}

interface LeaderboardEntry {
  score: number;
  totalQuestions: number;
  playedAt: string;
  username: string;
}

type QuizState = 'idle' | 'playing' | 'finished';

export default function QuizPage() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [quizState, setQuizState] = useState<QuizState>('idle');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ correct: boolean; correctAnswer: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([
      api.get('/api/quiz/questions').then((r) => setQuestions(r.data)),
      api.get('/api/quiz/leaderboard').then((r) => setLeaderboard(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const startQuiz = () => {
    setQuizState('playing');
    setCurrentIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setFeedback(null);
  };

  const handleAnswer = async (answer: string) => {
    if (selectedAnswer) return;
    setSelectedAnswer(answer);

    const res = await api.post('/api/quiz/answer', {
      questionId: questions[currentIndex].id,
      answer,
    });
    setFeedback(res.data);
    if (res.data.correct) setScore((s) => s + 1);
  };

  const nextQuestion = async () => {
    if (currentIndex + 1 >= questions.length) {
      // Quiz finished
      setQuizState('finished');
      if (user) {
        try {
          await api.post('/api/quiz/score', { score: score + (feedback?.correct ? 1 : 0), totalQuestions: questions.length });
          // Refresh leaderboard
          const lb = await api.get('/api/quiz/leaderboard');
          setLeaderboard(lb.data);
        } catch { /* ignore */ }
      }
    } else {
      setCurrentIndex((i) => i + 1);
      setSelectedAnswer(null);
      setFeedback(null);
    }
  };

  if (loading) return <div className="page-loading">Indlæser quiz... 🧠</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🧠 Quiz</h1>
      </div>

      <div className="quiz-layout">
        <div className="quiz-main">
          {quizState === 'idle' && (
            <div className="card quiz-start-card">
              <h2>Klar til at teste din viden?</h2>
              <p>{questions.length} spørgsmål venter dig!</p>
              <button className="btn btn-primary btn-lg" onClick={startQuiz}>
                Start quiz 🚀
              </button>
            </div>
          )}

          {quizState === 'playing' && questions.length > 0 && (
            <div className="card quiz-card">
              <div className="quiz-progress">
                Spørgsmål {currentIndex + 1} / {questions.length}
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }} />
                </div>
              </div>

              <h2 className="quiz-question">{questions[currentIndex].question}</h2>

              <div className="quiz-options">
                {questions[currentIndex].options.map((opt) => {
                  let cls = 'quiz-option';
                  if (selectedAnswer) {
                    if (opt === feedback?.correctAnswer) cls += ' correct';
                    else if (opt === selectedAnswer && !feedback?.correct) cls += ' incorrect';
                    else cls += ' disabled';
                  }
                  return (
                    <button key={opt} className={cls} onClick={() => handleAnswer(opt)} disabled={!!selectedAnswer}>
                      {opt}
                    </button>
                  );
                })}
              </div>

              {feedback && (
                <div className={`quiz-feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
                  {feedback.correct ? '✅ Korrekt!' : `❌ Forkert! Det rigtige svar er: ${feedback.correctAnswer}`}
                  <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={nextQuestion}>
                    {currentIndex + 1 >= questions.length ? 'Se resultat' : 'Næste spørgsmål →'}
                  </button>
                </div>
              )}
            </div>
          )}

          {quizState === 'finished' && (
            <div className="card quiz-result-card">
              <div className="result-icon">{score === questions.length ? '🏆' : score > questions.length / 2 ? '🎉' : '😅'}</div>
              <h2>Quiz afsluttet!</h2>
              <p className="result-score">Du fik <strong>{score}</strong> ud af <strong>{questions.length}</strong> rigtige</p>
              {!user && <p className="result-hint">Log ind for at gemme din score!</p>}
              <button className="btn btn-primary btn-lg" onClick={startQuiz}>Spil igen 🔄</button>
            </div>
          )}
        </div>

        <div className="quiz-sidebar">
          <div className="card leaderboard-card">
            <h2>🏆 Topscorer</h2>
            {leaderboard.length === 0 ? (
              <p className="text-muted">Ingen scores endnu</p>
            ) : (
              <ol className="leaderboard-list">
                {leaderboard.map((entry, i) => (
                  <li key={i} className="leaderboard-entry">
                    <span className="lb-rank">#{i + 1}</span>
                    <span className="lb-name">{entry.username}</span>
                    <span className="lb-score">{entry.score}/{entry.totalQuestions}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
